import React, { useState } from 'react';
import {
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Package,
  Users,
  Building,
  Heart,
  ShoppingCart,
  Download,
  Printer,
  MessageSquare,
  ArrowRightLeft,
  ChevronDown,
  ChevronRight,
  Pill,
  User,
  Calendar,
  Clock,
  DollarSign,
  Shield,
  AlertCircle,
  Send,
  UserCheck,
  Activity,
  Zap,
  FileText,
  Hash,
  MapPin
} from 'lucide-react';
import { format, addDays } from 'date-fns';



interface StockTransfer {
  id: string;
  fromPharmacy: string;
  toPharmacy: string;
  medication: string;
  quantity: number;
  status: 'pending' | 'approved' | 'in_transit' | 'received' | 'rejected';
  requestDate: string;
  expectedDate?: string;
  reason: string;
}

interface Patient {
  id: string;
  name: string;
  dob: string;
  address: string;
  phone: string;
  allergies: string[];
  conditions: string[];
  medications: Medication[];
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
  approvedAt?: string;
  rejectedAt?: string;
}

interface Medication {
  id: string;
  name: string;
  genericName: string;
  strength: string;
  dosageForm: string;
  quantity: number;
  directions: string;
  prescriber: string;
  prescriberNumber: string;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
  approvedAt?: string;
  rejectedAt?: string;
  stockStatus: 'in_stock' | 'low_stock' | 'out_of_stock' | 'needs_transfer';
  warnings: string[];
  interactions: string[];
  cost: number;
  margin: number;
}

interface Order {
  id: string;
  orderNumber: string;
  customerId: string;
  customerName: string;
  customerType: 'aged_care' | 'ndis' | 'retail' | 'hospital';
  orderDate: string;
  requiredDate: string;
  status: 'draft' | 'submitted' | 'pharmacist_review' | 'approved' | 'rejected' | 'picking' | 'packed' | 'ready' | 'dispatched' | 'delivered';
  priority: 'routine' | 'high' | 'urgent';
  totalPatients: number;
  totalMedications: number;
  totalValue: number;
  specialInstructions: string;
  alerts: string[];
  patients: Patient[];
  expanded?: boolean;
  approvedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  // Pick & Pack status
  pickStatus: 'pending' | 'picking' | 'picked' | 'packing' | 'packed';
  pickedItems: number;
  packedItems: number;
  assignedTo?: string;
  // Label printing status
  labelStatus: 'pending' | 'printing' | 'printed';
  labelsPrinted: number;
  labelsRequired: number;
}

const OrderManagement: React.FC = () => {
  const [selectedCustomerType, setSelectedCustomerType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [expandedPatients, setExpandedPatients] = useState<Set<string>>(new Set());
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionTarget, setRejectionTarget] = useState<{type: 'order' | 'patient' | 'medication', id: string, name: string} | null>(null);
  
  // Pick & Pack and Label Printing state
  const [showPickPackModal, setShowPickPackModal] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [selectedOrderForAction, setSelectedOrderForAction] = useState<string | null>(null);

  // Enhanced mock orders with patients and medications
  const [orders, setOrders] = useState<Order[]>([
    {
      id: 'order-001',
      orderNumber: 'AC-2024-0915-001',
      customerId: 'cust-ac-001',
      customerName: 'Sunnydale Aged Care Facility',
      customerType: 'aged_care',
      orderDate: '2024-09-15T08:00:00Z',
      requiredDate: '2024-09-16T09:00:00Z',
      status: 'pharmacist_review',
      priority: 'routine',
      totalPatients: 3,
      totalMedications: 8,
      totalValue: 2847.50,
      specialInstructions: 'Webster packs for 28 days supply',
      alerts: ['S8 witness required', 'Drug interaction check needed'],
      pickStatus: 'pending',
      pickedItems: 0,
      packedItems: 0,
      labelStatus: 'pending',
      labelsPrinted: 0,
      labelsRequired: 8,
      patients: [
        {
          id: 'patient-001',
          name: 'Margaret Johnson',
          dob: '1945-03-15',
          address: '123 Care Street, Sydney NSW 2000',
          phone: '0412 345 678',
          allergies: ['Penicillin', 'Shellfish'],
          conditions: ['Hypertension', 'Type 2 Diabetes', 'High Cholesterol'],
          status: 'pending',
          medications: [
            {
              id: 'med-001',
              name: 'Atorvastatin 20mg',
              genericName: 'Atorvastatin',
              strength: '20mg',
              dosageForm: 'Tablet',
              quantity: 30,
              directions: 'Take 1 tablet daily with evening meal',
              prescriber: 'Dr. Sarah Wilson',
              prescriberNumber: 'AHPRA123456',
              status: 'pending',
              stockStatus: 'in_stock',
              warnings: ['Take with food'],
              interactions: ['Warfarin', 'Digoxin'],
              cost: 12.50,
              margin: 8.75
            },
            {
              id: 'med-002',
              name: 'Metformin 500mg',
              genericName: 'Metformin',
              strength: '500mg',
              dosageForm: 'Tablet',
              quantity: 60,
              directions: 'Take 1 tablet twice daily with meals',
              prescriber: 'Dr. Sarah Wilson',
              prescriberNumber: 'AHPRA123456',
              status: 'pending',
              stockStatus: 'low_stock',
              warnings: ['May cause stomach upset'],
              interactions: ['Alcohol', 'Contrast media'],
              cost: 8.90,
              margin: 6.23
            }
          ]
        },
        {
          id: 'patient-002',
          name: 'Robert Smith',
          dob: '1952-07-22',
          address: '456 Elder Lane, Sydney NSW 2000',
          phone: '0413 456 789',
          allergies: ['Sulfa drugs'],
          conditions: ['Heart Disease', 'GERD'],
          status: 'pending',
          medications: [
            {
              id: 'med-003',
              name: 'Lisinopril 10mg',
              genericName: 'Lisinopril',
              strength: '10mg',
              dosageForm: 'Tablet',
              quantity: 28,
              directions: 'Take 1 tablet daily in the morning',
              prescriber: 'Dr. Michael Chen',
              prescriberNumber: 'AHPRA789012',
              status: 'pending',
              stockStatus: 'in_stock',
              warnings: ['Monitor blood pressure'],
              interactions: ['Potassium supplements', 'NSAIDs'],
              cost: 15.20,
              margin: 10.64
            },
            {
              id: 'med-004',
              name: 'Omeprazole 20mg',
              genericName: 'Omeprazole',
              strength: '20mg',
              dosageForm: 'Capsule',
              quantity: 30,
              directions: 'Take 1 capsule daily before breakfast',
              prescriber: 'Dr. Sarah Wilson',
              prescriberNumber: 'AHPRA123456',
              status: 'pending',
              stockStatus: 'needs_transfer',
              warnings: ['Take on empty stomach'],
              interactions: ['Warfarin', 'Clopidogrel'],
              cost: 22.80,
              margin: 15.96
            }
          ]
        },
        {
          id: 'patient-003',
          name: 'Alice Brown',
          dob: '1948-11-08',
          address: '789 Senior Street, Sydney NSW 2000',
          phone: '0414 567 890',
          allergies: [],
          conditions: ['High Cholesterol', 'Osteoporosis'],
          status: 'pending',
          medications: [
            {
              id: 'med-005',
              name: 'Simvastatin 40mg',
              genericName: 'Simvastatin',
              strength: '40mg',
              dosageForm: 'Tablet',
              quantity: 30,
              directions: 'Take 1 tablet daily in the evening',
              prescriber: 'Dr. Jennifer Lee',
              prescriberNumber: 'AHPRA456789',
              status: 'pending',
              stockStatus: 'in_stock',
              warnings: ['Avoid grapefruit'],
              interactions: ['Grapefruit juice', 'Warfarin'],
              cost: 18.75,
              margin: 13.13
            },
            {
              id: 'med-006',
              name: 'Alendronate 70mg',
              genericName: 'Alendronate',
              strength: '70mg',
              dosageForm: 'Tablet',
              quantity: 4,
              directions: 'Take 1 tablet weekly on empty stomach',
              prescriber: 'Dr. Jennifer Lee',
              prescriberNumber: 'AHPRA456789',
              status: 'pending',
              stockStatus: 'in_stock',
              warnings: ['Take with full glass of water', 'Stay upright for 30 minutes'],
              interactions: ['Calcium supplements', 'Antacids'],
              cost: 45.60,
              margin: 31.92
            }
          ]
        }
      ]
    },
    {
      id: 'order-002',
      orderNumber: 'NDIS-2024-0915-002',
      customerId: 'cust-ndis-001',
      customerName: 'NDIS Support Services',
      customerType: 'ndis',
      orderDate: '2024-09-15T10:30:00Z',
      requiredDate: '2024-09-17T14:00:00Z',
      status: 'approved',
      priority: 'high',
      totalPatients: 2,
      totalMedications: 4,
      totalValue: 1245.80,
      specialInstructions: 'Individual patient packs with compliance aids',
      alerts: ['Cold chain medication included'],
      pickStatus: 'picking',
      pickedItems: 2,
      packedItems: 0,
      assignedTo: 'John Smith',
      labelStatus: 'printing',
      labelsPrinted: 2,
      labelsRequired: 4,
      patients: [
        {
          id: 'patient-004',
          name: 'David Wilson',
          dob: '1985-05-12',
          address: '321 Support Avenue, Sydney NSW 2000',
          phone: '0415 678 901',
          allergies: ['Latex'],
          conditions: ['Cerebral Palsy', 'Epilepsy'],
          status: 'pending',
          medications: [
            {
              id: 'med-007',
              name: 'Insulin Glargine 100u/ml',
              genericName: 'Insulin Glargine',
              strength: '100u/ml',
              dosageForm: 'Vial',
              quantity: 2,
              directions: 'Inject 20 units daily at bedtime',
              prescriber: 'Dr. Emma Thompson',
              prescriberNumber: 'AHPRA987654',
              status: 'approved',
              stockStatus: 'in_stock',
              warnings: ['Refrigerate', 'Check expiry date'],
              interactions: ['Alcohol', 'Beta-blockers'],
              cost: 89.50,
              margin: 62.65
            }
          ]
        },
        {
          id: 'patient-005',
          name: 'Sarah Davis',
          dob: '1992-08-30',
          address: '654 Care Road, Sydney NSW 2000',
          phone: '0416 789 012',
          allergies: ['Aspirin'],
          conditions: ['Autism Spectrum Disorder', 'Anxiety'],
          status: 'pending',
          medications: [
            {
              id: 'med-008',
              name: 'Sertraline 50mg',
              genericName: 'Sertraline',
              strength: '50mg',
              dosageForm: 'Tablet',
              quantity: 30,
              directions: 'Take 1 tablet daily in the morning',
              prescriber: 'Dr. Emma Thompson',
              prescriberNumber: 'AHPRA987654',
              status: 'approved',
              stockStatus: 'in_stock',
              warnings: ['May cause drowsiness', 'Take with food'],
              interactions: ['MAO inhibitors', 'Warfarin'],
              cost: 25.40,
              margin: 17.78
            }
          ]
        }
      ]
    },
    {
      id: 'order-003',
      orderNumber: 'RTL-2024-0915-003',
      customerId: 'cust-retail-001',
      customerName: 'John Smith',
      customerType: 'retail',
      orderDate: '2024-09-15T14:15:00Z',
      requiredDate: '2024-09-15T16:00:00Z',
      status: 'ready',
      priority: 'urgent',
      totalPatients: 1,
      totalMedications: 3,
      totalValue: 89.50,
      specialInstructions: 'Customer waiting',
      alerts: [],
      pickStatus: 'packed',
      pickedItems: 3,
      packedItems: 3,
      assignedTo: 'Sarah Johnson',
      labelStatus: 'printed',
      labelsPrinted: 3,
      labelsRequired: 3,
      patients: [
        {
          id: 'patient-006',
          name: 'John Smith',
          dob: '1975-12-03',
          address: '987 Main Street, Sydney NSW 2000',
          phone: '0417 890 123',
          allergies: ['Codeine'],
          conditions: ['Migraine', 'Back Pain'],
          status: 'pending',
          medications: [
            {
              id: 'med-009',
              name: 'Sumatriptan 50mg',
              genericName: 'Sumatriptan',
              strength: '50mg',
              dosageForm: 'Tablet',
              quantity: 6,
              directions: 'Take 1 tablet at onset of migraine',
              prescriber: 'Dr. James Brown',
              prescriberNumber: 'AHPRA654321',
              status: 'approved',
              stockStatus: 'in_stock',
              warnings: ['Do not exceed 200mg in 24 hours'],
              interactions: ['MAO inhibitors', 'Ergotamine'],
              cost: 35.20,
              margin: 24.64
            },
            {
              id: 'med-010',
              name: 'Ibuprofen 400mg',
              genericName: 'Ibuprofen',
              strength: '400mg',
              dosageForm: 'Tablet',
              quantity: 20,
              directions: 'Take 1-2 tablets every 6-8 hours as needed',
              prescriber: 'Dr. James Brown',
              prescriberNumber: 'AHPRA654321',
              status: 'approved',
              stockStatus: 'in_stock',
              warnings: ['Take with food', 'Do not exceed 2400mg daily'],
              interactions: ['Warfarin', 'Lithium'],
              cost: 12.80,
              margin: 8.96
            },
            {
              id: 'med-011',
              name: 'Paracetamol 500mg',
              genericName: 'Paracetamol',
              strength: '500mg',
              dosageForm: 'Tablet',
              quantity: 20,
              directions: 'Take 1-2 tablets every 4-6 hours as needed',
              prescriber: 'Dr. James Brown',
              prescriberNumber: 'AHPRA654321',
              status: 'approved',
              stockStatus: 'in_stock',
              warnings: ['Do not exceed 4000mg daily'],
              interactions: ['Warfarin'],
              cost: 4.50,
              margin: 3.15
            }
          ]
        }
      ]
    }
  ]);

  // Removed intelligent ordering alerts - now handled in dedicated Alerts section

  // Stock transfer requests - MedFast Pharmacy locations
  const stockTransfers: StockTransfer[] = [
    {
      id: 'transfer-001',
      fromPharmacy: 'MedFast Westfield',
      toPharmacy: 'MedFast Sydney Central',
      medication: 'Warfarin 5mg',
      quantity: 100,
      status: 'pending',
      requestDate: '2024-09-15T09:00:00Z',
      reason: 'Urgent aged care order requirement'
    },
    {
      id: 'transfer-002',
      fromPharmacy: 'MedFast Bondi Junction',
      toPharmacy: 'MedFast Sydney Central',
      medication: 'Insulin Glargine',
      quantity: 10,
      status: 'approved',
      requestDate: '2024-09-14T15:30:00Z',
      expectedDate: '2024-09-16T10:00:00Z',
      reason: 'Cold chain medication for NDIS patient'
    },
    {
      id: 'transfer-003',
      fromPharmacy: 'MedFast Sydney Central',
      toPharmacy: 'MedFast Parramatta',
      medication: 'Rosuvastatin 20mg',
      quantity: 50,
      status: 'in_transit',
      requestDate: '2024-09-14T11:00:00Z',
      expectedDate: '2024-09-16T14:00:00Z',
      reason: 'Stock balancing between stores'
    }
  ];

  const getCustomerTypeIcon = (type: string) => {
    switch (type) {
      case 'aged_care': return <Building className="h-5 w-5 text-blue-600" />;
      case 'ndis': return <Heart className="h-5 w-5 text-green-600" />;
      case 'retail': return <ShoppingCart className="h-5 w-5 text-purple-600" />;
      case 'hospital': return <Users className="h-5 w-5 text-red-600" />;
      default: return <Package className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'pharmacist_review': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'picking': return 'bg-purple-100 text-purple-800';
      case 'packed': return 'bg-indigo-100 text-indigo-800';
      case 'ready': return 'bg-emerald-100 text-emerald-800';
      case 'dispatched': return 'bg-teal-100 text-teal-800';
      case 'delivered': return 'bg-cyan-100 text-cyan-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };


  const getTransferStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'in_transit': return 'bg-blue-100 text-blue-800';
      case 'received': return 'bg-emerald-100 text-emerald-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStockStatusColor = (status: string) => {
    switch (status) {
      case 'in_stock': return 'bg-green-100 text-green-800';
      case 'low_stock': return 'bg-yellow-100 text-yellow-800';
      case 'out_of_stock': return 'bg-red-100 text-red-800';
      case 'needs_transfer': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStockStatusIcon = (status: string) => {
    switch (status) {
      case 'in_stock': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'low_stock': return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case 'out_of_stock': return <XCircle className="h-4 w-4 text-red-600" />;
      case 'needs_transfer': return <ArrowRightLeft className="h-4 w-4 text-orange-600" />;
      default: return <Package className="h-4 w-4 text-gray-600" />;
    }
  };

  const toggleOrderExpansion = (orderId: string) => {
    console.log('Toggling order expansion for:', orderId);
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
      console.log('Collapsing order:', orderId);
    } else {
      newExpanded.add(orderId);
      console.log('Expanding order:', orderId);
    }
    setExpandedOrders(newExpanded);
    console.log('New expanded orders:', Array.from(newExpanded));
  };

  const togglePatientExpansion = (patientId: string) => {
    const newExpanded = new Set(expandedPatients);
    if (newExpanded.has(patientId)) {
      newExpanded.delete(patientId);
    } else {
      newExpanded.add(patientId);
    }
    setExpandedPatients(newExpanded);
  };

  const handleApprove = (type: 'order' | 'patient' | 'medication', id: string) => {
    console.log(`Approved ${type}: ${id}`);
    
    // Update the orders state with the new approval
    setOrders(prevOrders => 
      prevOrders.map(order => {
        if (type === 'order' && order.id === id) {
          return {
            ...order,
            status: 'approved' as const,
            approvedAt: new Date().toISOString()
          };
        }
        
        if (type === 'patient') {
          return {
            ...order,
            patients: order.patients.map(patient => 
              patient.id === id 
                ? { ...patient, status: 'approved' as const, approvedAt: new Date().toISOString() }
                : patient
            )
          };
        }
        
        if (type === 'medication') {
          return {
            ...order,
            patients: order.patients.map(patient => ({
              ...patient,
              medications: patient.medications.map(med => 
                med.id === id 
                  ? { ...med, status: 'approved' as const, approvedAt: new Date().toISOString() }
                  : med
              )
            }))
          };
        }
        
        return order;
      })
    );
    
    alert(`Approved ${type}: ${id}`);
  };

  const handleReject = (type: 'order' | 'patient' | 'medication', id: string, name: string) => {
    setRejectionTarget({ type, id, name });
    setShowRejectionModal(true);
  };

  const submitRejection = () => {
    if (rejectionTarget && rejectionReason) {
      console.log(`Rejected ${rejectionTarget.type}: ${rejectionTarget.id} - Reason: ${rejectionReason}`);
      
      // Update the orders state with the new rejection
      setOrders(prevOrders => 
        prevOrders.map(order => {
          if (rejectionTarget.type === 'order' && order.id === rejectionTarget.id) {
            return {
              ...order,
              status: 'rejected' as const,
              rejectedAt: new Date().toISOString(),
              rejectionReason: rejectionReason
            };
          }
          
          if (rejectionTarget.type === 'patient') {
            return {
              ...order,
              patients: order.patients.map(patient => 
                patient.id === rejectionTarget.id 
                  ? { 
                      ...patient, 
                      status: 'rejected' as const, 
                      rejectedAt: new Date().toISOString(),
                      rejectionReason: rejectionReason
                    }
                  : patient
              )
            };
          }
          
          if (rejectionTarget.type === 'medication') {
            return {
              ...order,
              patients: order.patients.map(patient => ({
                ...patient,
                medications: patient.medications.map(med => 
                  med.id === rejectionTarget.id 
                    ? { 
                        ...med, 
                        status: 'rejected' as const, 
                        rejectedAt: new Date().toISOString(),
                        rejectionReason: rejectionReason
                      }
                    : med
                )
              }))
            };
          }
          
          return order;
        })
      );
      
      setShowRejectionModal(false);
      setRejectionReason('');
      setRejectionTarget(null);
    }
  };

  // Pick & Pack functions
  const handleStartPicking = (orderId: string) => {
    setOrders(prevOrders => 
      prevOrders.map(order => 
        order.id === orderId 
          ? { ...order, pickStatus: 'picking' as const, assignedTo: 'Current User' }
          : order
      )
    );
    setShowPickPackModal(false);
  };

  const handleCompletePicking = (orderId: string) => {
    setOrders(prevOrders => 
      prevOrders.map(order => 
        order.id === orderId 
          ? { ...order, pickStatus: 'picked' as const, pickedItems: order.totalMedications }
          : order
      )
    );
  };

  const handleStartPacking = (orderId: string) => {
    setOrders(prevOrders => 
      prevOrders.map(order => 
        order.id === orderId 
          ? { ...order, pickStatus: 'packing' as const }
          : order
      )
    );
  };

  const handleCompletePacking = (orderId: string) => {
    setOrders(prevOrders => 
      prevOrders.map(order => 
        order.id === orderId 
          ? { ...order, pickStatus: 'packed' as const, packedItems: order.totalMedications, status: 'ready' as const }
          : order
      )
    );
  };

  // Label Printing functions
  const handlePrintLabels = (orderId: string) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;

    // Generate label content for approved medications only
    const approvedMedications = order.patients.flatMap(patient => 
      patient.medications.filter(med => med.status === 'approved')
    );

    const labelContent = generateLabelContent(order, approvedMedications);
    
    // Open print window
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(labelContent);
      printWindow.document.close();
      printWindow.print();
    }

    // Update label status
    setOrders(prevOrders => 
      prevOrders.map(o => 
        o.id === orderId 
          ? { ...o, labelStatus: 'printed' as const, labelsPrinted: approvedMedications.length }
          : o
      )
    );
    
    setShowLabelModal(false);
  };

  const generateLabelContent = (order: Order, medications: Medication[]) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>MedFast Pharmacy - Medication Labels</title>
        <style>
          @page { margin: 0.5in; }
          body { font-family: Arial, sans-serif; font-size: 12px; }
          .label { 
            border: 2px solid #000; 
            padding: 12px; 
            margin: 10px 0; 
            page-break-inside: avoid;
            background: white;
            border-radius: 4px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            max-width: 400px;
          }
          .label-header { 
            background: #6B46C1; 
            color: white; 
            padding: 8px; 
            margin: -12px -12px 8px -12px; 
            border-radius: 4px 4px 0 0;
            font-weight: bold; 
            font-size: 14px; 
          }
          .medication { font-size: 16px; font-weight: bold; color: #1F2937; margin: 8px 0; }
          .patient { font-size: 12px; margin: 4px 0; }
          .directions { font-size: 11px; margin: 8px 0; padding: 8px; background: #F3F4F6; border-radius: 4px; }
          .warnings { font-size: 10px; color: #DC2626; font-weight: bold; margin: 5px 0; }
          .barcode { text-align: center; font-family: monospace; margin: 8px 0; font-size: 12px; }
          .footer { font-size: 9px; color: #6B7280; margin-top: 8px; }
        </style>
      </head>
      <body>
        <div style="text-align: center; margin-bottom: 20px;">
          <h1>🏥 MedFast Pharmacy - Medication Labels</h1>
          <p><strong>Order:</strong> ${order.orderNumber} | <strong>Customer:</strong> ${order.customerName}</p>
        </div>
        
        ${medications.map(med => `
          <div class="label">
            <div class="label-header">MedFast Pharmacy</div>
            <div class="medication">${med.name}</div>
            <div class="patient"><strong>Patient:</strong> ${order.patients.find(p => p.medications.some(m => m.id === med.id))?.name || 'Unknown'}</div>
            <div class="patient"><strong>DOB:</strong> ${order.patients.find(p => p.medications.some(m => m.id === med.id))?.dob || 'Unknown'}</div>
            <div class="patient"><strong>Prescriber:</strong> ${med.prescriber}</div>
            <div class="directions"><strong>Directions:</strong> ${med.directions}</div>
            <div class="warnings">${med.warnings.join(' | ')}</div>
            <div class="barcode">${med.prescriberNumber}</div>
            <div class="footer">Dispensed: ${format(new Date(), 'dd/MM/yyyy')} | MedFast Pharmacy</div>
          </div>
        `).join('')}
      </body>
      </html>
    `;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Order Management
          </h1>
          <p className="text-gray-600 mt-2">Manage orders across aged care, NDIS, and retail customers</p>
        </div>
        <div className="flex space-x-3">
          <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>New Order</span>
          </button>
          <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center space-x-2">
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
        </div>
      </div>


      {/* Filters and Search */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search orders, customers..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={selectedCustomerType}
            onChange={(e) => setSelectedCustomerType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Customer Types</option>
            <option value="aged_care">Aged Care</option>
            <option value="ndis">NDIS</option>
            <option value="retail">Retail</option>
            <option value="hospital">Hospital</option>
          </select>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="submitted">Submitted</option>
            <option value="pharmacist_review">Pharmacist Review</option>
            <option value="approved">Approved</option>
            <option value="picking">Picking</option>
            <option value="ready">Ready</option>
          </select>

          <button className="flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Filter className="h-4 w-4" />
            <span>More Filters</span>
          </button>
        </div>
      </div>

      {/* Enhanced Orders List with Expansion */}
      <div className="space-y-4">
        {orders.map((order) => (
          <div key={order.id} className="group bg-white/80 backdrop-blur-md rounded-3xl shadow-xl border border-white/30 p-8 hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] hover:-translate-y-1">
            {/* Order Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => toggleOrderExpansion(order.id)}
                  className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {expandedOrders.has(order.id) ? (
                    <ChevronDown className="h-5 w-5 text-gray-600" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-600" />
                  )}
                </button>
                <div className="flex items-center space-x-3">
                  {getCustomerTypeIcon(order.customerType)}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{order.orderNumber}</h3>
                    <p className="text-sm text-gray-600">{order.customerName}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(order.status)}`}>
                    {order.status.replace('_', ' ').toUpperCase()}
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                    order.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                    order.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {order.priority.toUpperCase()}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <button className="p-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-all duration-300 hover:scale-110 group/btn">
                  <Eye className="h-5 w-5 group-hover/btn:scale-110 transition-transform duration-300" />
                </button>
                <button className="p-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-all duration-300 hover:scale-110 group/btn">
                  <Edit className="h-5 w-5 group-hover/btn:scale-110 transition-transform duration-300" />
                </button>
                
                {/* Pick & Pack Button */}
                {order.status === 'approved' && (
                  <button 
                    onClick={() => {
                      setSelectedOrderForAction(order.id);
                      setShowPickPackModal(true);
                    }}
                    className="p-3 text-blue-600 hover:text-blue-800 hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 rounded-xl transition-all duration-300 hover:scale-110 group/btn shadow-lg hover:shadow-blue-200"
                    title="Pick & Pack"
                  >
                    <Package className="h-5 w-5 group-hover/btn:scale-110 transition-transform duration-300" />
                  </button>
                )}
                
                {/* Label Printing Button */}
                {order.status === 'approved' && (
                  <button 
                    onClick={() => {
                      setSelectedOrderForAction(order.id);
                      setShowLabelModal(true);
                    }}
                    className="p-3 text-purple-600 hover:text-purple-800 hover:bg-gradient-to-r hover:from-purple-50 hover:to-purple-100 rounded-xl transition-all duration-300 hover:scale-110 group/btn shadow-lg hover:shadow-purple-200"
                    title="Print Labels"
                  >
                    <FileText className="h-5 w-5 group-hover/btn:scale-110 transition-transform duration-300" />
                  </button>
                )}
              </div>
            </div>

            {/* Order Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-sm text-gray-600">Order Date</p>
                <p className="font-medium">{format(new Date(order.orderDate), 'dd/MM/yyyy HH:mm')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Required Date</p>
                <p className="font-medium">{format(new Date(order.requiredDate), 'dd/MM/yyyy HH:mm')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Patients/Items</p>
                <p className="font-medium">{order.totalPatients} patients • {order.totalMedications} medications</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Value</p>
                <p className="font-medium text-lg">${order.totalValue.toLocaleString()}</p>
              </div>
            </div>

            {/* Pick & Pack and Label Status */}
            {order.status === 'approved' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-gray-200">
                <div className="group flex items-center space-x-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                  <div className="p-3 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl group-hover:scale-110 transition-transform duration-300">
                    <Package className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Pick & Pack Status</p>
                    <div className="flex items-center space-x-3 mb-2">
                      <span className={`px-3 py-1.5 text-xs font-bold rounded-full shadow-sm ${
                        order.pickStatus === 'pending' ? 'bg-gray-100 text-gray-800' :
                        order.pickStatus === 'picking' ? 'bg-blue-100 text-blue-800' :
                        order.pickStatus === 'picked' ? 'bg-yellow-100 text-yellow-800' :
                        order.pickStatus === 'packing' ? 'bg-orange-100 text-orange-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {order.pickStatus.toUpperCase()}
                      </span>
                      <span className="text-sm text-gray-600 font-medium">
                        {order.pickedItems}/{order.totalMedications} picked • {order.packedItems}/{order.totalMedications} packed
                      </span>
                    </div>
                    {order.assignedTo && (
                      <p className="text-xs text-gray-500 font-medium">Assigned to: {order.assignedTo}</p>
                    )}
                  </div>
                </div>
                
                <div className="group flex items-center space-x-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                  <div className="p-3 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl group-hover:scale-110 transition-transform duration-300">
                    <FileText className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Label Status</p>
                    <div className="flex items-center space-x-3">
                      <span className={`px-3 py-1.5 text-xs font-bold rounded-full shadow-sm ${
                        order.labelStatus === 'pending' ? 'bg-gray-100 text-gray-800' :
                        order.labelStatus === 'printing' ? 'bg-blue-100 text-blue-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {order.labelStatus.toUpperCase()}
                      </span>
                      <span className="text-sm text-gray-600 font-medium">
                        {order.labelsPrinted}/{order.labelsRequired} printed
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Alerts */}
            {order.alerts.length > 0 && (
              <div className="mb-4">
                <div className="flex flex-wrap gap-2">
                  {order.alerts.map((alert, index) => (
                    <span key={index} className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full flex items-center space-x-1">
                      <AlertTriangle className="h-3 w-3" />
                      <span>{alert}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Order Actions */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">{order.specialInstructions}</p>
              <div className="flex space-x-2">
                {order.status === 'pharmacist_review' && (
                  <>
                    <button 
                      onClick={() => handleApprove('order', order.id)}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
                    >
                      <CheckCircle className="h-4 w-4" />
                      <span>Approve Order</span>
                    </button>
                    <button 
                      onClick={() => handleReject('order', order.id, order.orderNumber)}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center space-x-2"
                    >
                      <XCircle className="h-4 w-4" />
                      <span>Reject Order</span>
                    </button>
                  </>
                )}
                {order.status === 'approved' && (
                  <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2">
                    <Package className="h-4 w-4" />
                    <span>Start Picking</span>
                  </button>
                )}
                {order.status === 'ready' && (
                  <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2">
                    <MessageSquare className="h-4 w-4" />
                    <span>Notify Customer</span>
                  </button>
                )}
              </div>
            </div>

            {/* Expanded Order Details */}
            {expandedOrders.has(order.id) && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Users className="h-5 w-5" />
                  <span>Patients & Medications ({order.patients.length} patients)</span>
                </h4>
                
                <div className="space-y-4">
                  {order.patients.map((patient) => (
                    <div key={patient.id} className="bg-gray-50 rounded-lg p-4">
                      {/* Patient Header */}
                      <div className="flex items-center justify-between mb-3">
                        <button
                          onClick={() => togglePatientExpansion(patient.id)}
                          className="flex items-center space-x-2 hover:bg-gray-100 p-2 rounded-lg transition-colors"
                        >
                          {expandedPatients.has(patient.id) ? (
                            <ChevronDown className="h-4 w-4 text-gray-600" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-600" />
                          )}
                          <User className="h-4 w-4 text-blue-600" />
                          <span className="font-medium text-gray-900">{patient.name}</span>
                          <span className="text-sm text-gray-500">({patient.medications.length} medications)</span>
                        </button>
                        
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => handleApprove('patient', patient.id)}
                            className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 flex items-center space-x-1"
                          >
                            <CheckCircle className="h-3 w-3" />
                            <span>Approve All</span>
                          </button>
                          <button 
                            onClick={() => handleReject('patient', patient.id, patient.name)}
                            className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 flex items-center space-x-1"
                          >
                            <XCircle className="h-3 w-3" />
                            <span>Reject All</span>
                          </button>
                        </div>
                      </div>

                      {/* Patient Info */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3 text-sm">
                        <div>
                          <span className="text-gray-600">DOB:</span> {format(new Date(patient.dob), 'dd/MM/yyyy')}
                        </div>
                        <div>
                          <span className="text-gray-600">Phone:</span> {patient.phone}
                        </div>
                        <div>
                          <span className="text-gray-600">Allergies:</span> {patient.allergies.length > 0 ? patient.allergies.join(', ') : 'None'}
                        </div>
                      </div>

                      {/* Expanded Medications */}
                      {expandedPatients.has(patient.id) && (
                        <div className="mt-4 space-y-3">
                          {patient.medications.map((medication) => (
                            <div key={medication.id} className="bg-white rounded-lg p-4 border border-gray-200">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center space-x-3">
                                  <Pill className="h-4 w-4 text-purple-600" />
                                  <div>
                                    <h5 className="font-medium text-gray-900">{medication.name}</h5>
                                    <p className="text-sm text-gray-600">{medication.genericName} • {medication.strength} • {medication.dosageForm}</p>
                                  </div>
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                  <div className={`px-2 py-1 text-xs font-medium rounded flex items-center space-x-1 ${getStockStatusColor(medication.stockStatus)}`}>
                                    {getStockStatusIcon(medication.stockStatus)}
                                    <span>{medication.stockStatus.replace('_', ' ').toUpperCase()}</span>
                                  </div>
                                  
                                  {medication.status === 'pending' && (
                                    <div className="flex space-x-1">
                                      <button 
                                        onClick={() => handleApprove('medication', medication.id)}
                                        className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700 flex items-center space-x-1"
                                      >
                                        <CheckCircle className="h-3 w-3" />
                                        <span>Approve</span>
                                      </button>
                                      <button 
                                        onClick={() => handleReject('medication', medication.id, medication.name)}
                                        className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700 flex items-center space-x-1"
                                      >
                                        <XCircle className="h-3 w-3" />
                                        <span>Reject</span>
                                      </button>
                                    </div>
                                  )}
                                  
                                  {medication.status === 'approved' && (
                                    <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800 flex items-center space-x-1">
                                      <CheckCircle className="h-3 w-3" />
                                      <span>APPROVED</span>
                                    </span>
                                  )}
                                  
                                  {medication.status === 'rejected' && (
                                    <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-800 flex items-center space-x-1">
                                      <XCircle className="h-3 w-3" />
                                      <span>REJECTED</span>
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-600">Quantity:</span> {medication.quantity}
                                </div>
                                <div>
                                  <span className="text-gray-600">Prescriber:</span> {medication.prescriber}
                                </div>
                                <div>
                                  <span className="text-gray-600">Directions:</span> {medication.directions}
                                </div>
                                <div>
                                  <span className="text-gray-600">Cost:</span> ${medication.cost.toFixed(2)} (Margin: ${medication.margin.toFixed(2)})
                                </div>
                              </div>

                              {medication.warnings.length > 0 && (
                                <div className="mt-2">
                                  <div className="flex flex-wrap gap-1">
                                    {medication.warnings.map((warning, index) => (
                                      <span key={index} className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded flex items-center space-x-1">
                                        <AlertTriangle className="h-3 w-3" />
                                        <span>{warning}</span>
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {medication.interactions.length > 0 && (
                                <div className="mt-2">
                                  <span className="text-xs text-gray-600">Interactions:</span>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {medication.interactions.map((interaction, index) => (
                                      <span key={index} className="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded">
                                        {interaction}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Reject {rejectionTarget?.type === 'order' ? 'Order' : rejectionTarget?.type === 'patient' ? 'Patient' : 'Medication'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {rejectionTarget?.name}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Rejection
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                rows={3}
                placeholder="Please provide a reason for rejection..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRejectionModal(false);
                  setRejectionReason('');
                  setRejectionTarget(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={submitRejection}
                disabled={!rejectionReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit Rejection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pick & Pack Modal */}
      {showPickPackModal && selectedOrderForAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Pick & Pack Order
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Order: {orders.find(o => o.id === selectedOrderForAction)?.orderNumber}
            </p>
            <div className="space-y-3">
              <button
                onClick={() => handleStartPicking(selectedOrderForAction)}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Start Picking
              </button>
              <button
                onClick={() => handleCompletePicking(selectedOrderForAction)}
                className="w-full px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
              >
                Complete Picking
              </button>
              <button
                onClick={() => handleStartPacking(selectedOrderForAction)}
                className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
              >
                Start Packing
              </button>
              <button
                onClick={() => handleCompletePacking(selectedOrderForAction)}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Complete Packing
              </button>
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => {
                  setShowPickPackModal(false);
                  setSelectedOrderForAction(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Label Printing Modal */}
      {showLabelModal && selectedOrderForAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Print Medication Labels
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Order: {orders.find(o => o.id === selectedOrderForAction)?.orderNumber}
            </p>
            <p className="text-sm text-gray-500 mb-4">
              This will print labels for all approved medications in this order.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowLabelModal(false);
                  setSelectedOrderForAction(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => handlePrintLabels(selectedOrderForAction)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Print Labels
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default OrderManagement;
